/**
 * contractor-directory.ts
 * Shared Zod contracts for the `kernel-contractor-directory` capability —
 * "Mis Contratistas": partnership initiation keyed by organization id
 * (never `identity.client`), plus the directory listing/search/register
 * contracts added by later slices of this change.
 *
 * Slice A (PR1): the organization-id-keyed initiation body.
 * Slice B (this file, this PR): the directory row DTO returned by
 * `GET /identity/contractor-directory(/search)` (design.md CD1/CD2).
 * Later slices append the find-or-create-then-link (CD9) contracts — do not
 * add them ahead of their own tasks.
 *
 * Spec: kernel-tenant-partnership / "Organization-Id-Keyed Initiation Is a
 *       First-Class Entry Point"
 *       kernel-contractor-directory / "Partnership Initiation Keyed by
 *       Organization Id"
 *       kernel-contractor-directory / "Tenant-Scoped Counterparty Directory
 *       Listing"
 *       kernel-contractor-directory / "Explicit Search to Add a New
 *       Counterparty (CUIT or Legal Name)"
 * Design: design.md CD1, CD2, CD3
 */
import { z } from 'zod';
import { OrganizationKindSchema, OrganizationSchema, TenantPartnershipScopeSchema, TenantPartnershipStatusSchema } from './organization.js';

export const RequestPartnershipByOrganizationBodySchema = z.object({
  organizationId: z.string().uuid(),
  scope: TenantPartnershipScopeSchema.default('locx.work_permit'),
});

export type RequestPartnershipByOrganizationBody = z.infer<typeof RequestPartnershipByOrganizationBodySchema>;

// ---------------------------------------------------------------------------
// Slice B — directory listing/search row (design.md CD1, "Interfaces / Contracts")
// ---------------------------------------------------------------------------

export const ContractorDirectoryRowSchema = z.object({
  partnershipId: z.string().uuid(),
  status: TenantPartnershipStatusSchema,
  scope: TenantPartnershipScopeSchema,
  /**
   * The caller's side of THIS row. Day one was always 'counterparty' (CD8's
   * extension point) — CD9's auto-link-by-CUIT made 'owner' reachable in
   * practice: whoever's CUIT gets registered/requested by another tenant
   * ends up 'owner' on their OWN copy of this same row.
   */
  myRole: z.enum(['owner', 'counterparty']),
  /**
   * Real user report 2026-09-10: "Aceptar" was offered on every pending row
   * regardless of which side could actually use it — the initiating side
   * always got a 409 ("the initiating side cannot accept its own request"),
   * indistinguishable in the UI from a row they could genuinely act on.
   * Mirrors acceptPartnership's own `requestedByCompanyId !== callerId` check
   * (partnership-resolver.ts) — never re-derive that rule in the frontend.
   */
  canAccept: z.boolean(),
  counterpartyCompanyId: z.string().uuid(),
  /** Resolved via lookupCompanyById; null only if the company row itself vanished. */
  counterpartyCompanyName: z.string().nullable(),
  /** null when the counterparty company has no identity.organization row (CD1/CD5). */
  organization: OrganizationSchema.nullable(),
  requestedAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
});

export type ContractorDirectoryRow = z.infer<typeof ContractorDirectoryRowSchema>;

export const ContractorDirectoryListResponseSchema = z.object({
  items: z.array(ContractorDirectoryRowSchema),
});

export type ContractorDirectoryListResponse = z.infer<typeof ContractorDirectoryListResponseSchema>;

// ---------------------------------------------------------------------------
// Slice B2 (PR3) — tenant-triggered auto-link by CUIT, find-or-create-then-
// link (design.md CD9). NO linkedCompanyId / companyId field on the body —
// the linked company id can only come from an exact normalized-CUIT match on
// identity.company, never from the caller (CD9.c#1).
// ---------------------------------------------------------------------------

export const RegisterCounterpartyBodySchema = z.object({
  /** Normalized server-side (normalizeCuit) before any lookup or write. */
  cuit: z.string().min(1),
  /** Required only when the counterparty organization does not exist yet. */
  legalName: z.string().min(1).optional(),
  kind: OrganizationKindSchema.default('contractor'),
});

export type RegisterCounterpartyBody = z.infer<typeof RegisterCounterpartyBodySchema>;

export const CounterpartyLinkStatusSchema = z.enum(['linked', 'already-linked', 'unlinked']);
export type CounterpartyLinkStatus = z.infer<typeof CounterpartyLinkStatusSchema>;

export const RegisterCounterpartyResponseSchema = z.object({
  organization: OrganizationSchema,
  linkStatus: CounterpartyLinkStatusSchema,
  /**
   * false means an organization already existed under this CUIT and was
   * REUSED, never duplicated (real user report 2026-09-10: unclear feedback
   * read as "no valida que ya existe, puede duplicar la contratista" — the
   * backend already deduplicated by CUIT, this field lets the UI say so).
   */
  created: z.boolean(),
});

export type RegisterCounterpartyResponse = z.infer<typeof RegisterCounterpartyResponseSchema>;

// ---------------------------------------------------------------------------
// Slice B3 — read-only tenant-match hint on `GET .../search` (design.md
// CD9.b: search stays read-only, never auto-links; it only REPORTS whether
// the searched CUIT belongs to a real Suite tenant, even when no
// identity.organization row exists yet for it).
// ---------------------------------------------------------------------------

export const ContractorDirectoryTenantMatchSchema = z.object({
  companyId: z.string().uuid(),
  companyName: z.string(),
});

export type ContractorDirectoryTenantMatch = z.infer<typeof ContractorDirectoryTenantMatchSchema>;

export const ContractorDirectorySearchResponseSchema = z.object({
  items: z.array(OrganizationSchema),
  totalCount: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /**
   * Non-null only when `cuit` was provided AND it matches exactly one ACTIVE
   * identity.company row. Null when no cuit was searched, 0 companies match,
   * or 2+ companies match (an ambiguous CUIT is a data defect a human must
   * resolve — never guessed here).
   */
  tenantMatch: ContractorDirectoryTenantMatchSchema.nullable(),
});

export type ContractorDirectorySearchResponse = z.infer<typeof ContractorDirectorySearchResponseSchema>;
